// src/components/PhotoGalleryModal.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import "../scss/components/_photogallerymodal.scss";

const TAB_ORDER = ["PG", "SINGLE", "DOUBLE", "TRIPLE", "QUAD", "DORM"];

const formatTypeName = (type) => {
  if (type === "PG") return "Common";
  if (type === "SINGLE") return "Single Sharing";
  if (type === "DOUBLE") return "Double Sharing";
  if (type === "TRIPLE") return "Triple Sharing";
  if (type === "QUAD") return "Quad Sharing";
  if (type === "DORM") return "Dorm Sharing";
  return type;
};

const typeBadgeLetter = (type) => {
  if (type === "SINGLE") return "S";
  if (type === "DOUBLE") return "D";
  if (type === "TRIPLE") return "T";
  if (type === "QUAD") return "Q";
  if (type === "DORM") return "Do";
  return null;
};

// ─── Media type detection ───────────────────────────────────────────
// The API mixes videos into the same photo list/PhotoType (e.g. "PG"),
// so a video is identified purely by its file extension.
const VIDEO_EXTENSIONS = [
  ".mp4", ".webm", ".mov", ".m4v", ".ogv", ".ogg",
  ".avi", ".wmv", ".flv", ".mkv", ".3gp", ".3g2", ".mpeg", ".mpg",
];
const IMAGE_EXTENSIONS = [
  ".png", ".jpg", ".jpeg", ".gif", ".svg", ".webp", ".avif",
  ".bmp", ".tiff", ".tif", ".heic", ".heif", ".ico",
];

const getUrlExtension = (url = "") => {
  const clean = url.split("?")[0].split("#")[0].toLowerCase();
  const match = clean.match(/\.[a-z0-9]+$/);
  return match ? match[0] : "";
};

const isVideoUrl = (url = "") => VIDEO_EXTENSIONS.includes(getUrlExtension(url));
const isImageUrl = (url = "") => IMAGE_EXTENSIONS.includes(getUrlExtension(url));

// Falls back to <img> for anything unrecognized (safer than guessing
// video), but logs it so a genuinely new file type gets noticed.
const warnIfUnrecognizedMediaType = (url) => {
  if (!url || isVideoUrl(url) || isImageUrl(url)) return;
  console.warn(`[PhotoGalleryModal] Unrecognized file extension, defaulting to <img>: ${url}`);
};

// ─── Video poster-frame selection ──────────────────────────────────
// Picks a frame as a FRACTION of each video's own duration rather than
// a fixed number of seconds, so it self-adjusts regardless of how long
// a given video is. If canvas pixel sampling is available (see the
// crossOrigin note on VideoThumb below), we go further and skip
// candidate frames that come back essentially black.
// Identical approach to pgdetail.js — kept in sync intentionally.
const POSTER_CANDIDATE_FRACTIONS = [0.1, 0.25, 0.4, 0.6, 0.8];
const POSTER_BLACK_THRESHOLD = 16;

const sampleFrameBrightness = (video) => {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 32;
    canvas.height = 18;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, 32, 18);
    const { data } = ctx.getImageData(0, 0, 32, 18);
    let total = 0;
    for (let i = 0; i < data.length; i += 4) {
      total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
    }
    return total / (data.length / 4);
  } catch (e) {
    return null; // cross-origin canvas without CORS — can't sample, accept the frame
  }
};

const pickBestPosterFrame = (video, fractions = POSTER_CANDIDATE_FRACTIONS) => {
  const duration = video.duration;
  if (!duration || !isFinite(duration)) return;

  let i = 0;
  const seekToNext = () => {
    if (i >= fractions.length) return;
    const t = Math.min(duration - 0.1, Math.max(0.1, duration * fractions[i]));
    video.currentTime = t;
  };

  const onSeeked = () => {
    const brightness = sampleFrameBrightness(video);
    const goodEnough = brightness === null || brightness > POSTER_BLACK_THRESHOLD;
    if (goodEnough || i === fractions.length - 1) {
      video.removeEventListener("seeked", onSeeked);
      return;
    }
    i += 1;
    seekToNext();
  };

  video.addEventListener("seeked", onSeeked);
  seekToNext();
};

// Non-autoplaying video preview used in the masonry grid and the
// lightbox thumbnail strip. Browsers show a blank/black box for a
// <video> with no autoplay until it's played, so — unless a real
// `poster` image is supplied — we auto-pick a frame (see
// pickBestPosterFrame above). Pass `posterTime` (seconds) to force an
// exact frame instead, or `poster` for an actual image URL (best
// option if the API ever returns one alongside PhotoURL). Pass
// `crossOrigin="anonymous"` only if your video storage sends CORS
// headers — required for brightness-based black-frame skipping to
// activate; without it, the duration-fraction default still applies.
const VideoThumb = ({ src, className, poster, posterTime, crossOrigin }) => {
  const ref = useRef(null);

  useEffect(() => {
    warnIfUnrecognizedMediaType(src);
  }, [src]);

  useEffect(() => {
    if (poster) return; // real poster image already covers this
    const v = ref.current;
    if (!v) return;
    const onLoadedMetadata = () => {
      if (posterTime != null) {
        try { v.currentTime = posterTime; } catch (e) { /* ignore */ }
        return;
      }
      pickBestPosterFrame(v);
    };
    v.addEventListener("loadedmetadata", onLoadedMetadata);
    return () => v.removeEventListener("loadedmetadata", onLoadedMetadata);
  }, [src, poster, posterTime]);

  return (
    <video
      ref={ref}
      src={src}
      poster={poster}
      crossOrigin={crossOrigin}
      className={className}
      muted
      loop
      playsInline
      preload="metadata"
    />
  );
};

const PhotoGalleryModal = ({ photos = [], onClose, initialTab = "All", initialViewMode = "grid", initialSelectedPhotoId = null }) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [viewMode, setViewMode] = useState(initialViewMode); // "grid" | "lightbox"
  // Match by PhotoID — the actual unique key the API gives every photo —
  // rather than URL string matching or raw array position. Computed
  // once via lazy init since the component fully remounts each time it
  // opens, so this always reflects the photo that was actually clicked.
  const [selectedIndex, setSelectedIndex] = useState(() => {
    if (initialSelectedPhotoId == null) return 0;
    const idx = photos.findIndex((p) => p.PhotoID === initialSelectedPhotoId);
    console.log("[PhotoGalleryModal] resolving initialSelectedPhotoId →", {
      initialSelectedPhotoId,
      matchedIndex: idx,
      matchedPhoto: idx >= 0 ? photos[idx] : null,
      allPhotoIds: photos.map((p) => p.PhotoID),
    });
    return idx >= 0 ? idx : 0;
  });

  // Lightbox interaction state
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const overlayRef = useRef(null);
  const imageContainerRef = useRef(null);
  const thumbnailContainerRef = useRef(null);
  const gridScrollRef = useRef(null);

  // ── Grouping / filtering ─────────────────────────────────────────
  const groupedPhotos = React.useMemo(() => {
    const groups = {};
    photos.forEach((p) => {
      const t = p.PhotoType || "Other";
      (groups[t] ||= []).push(p);
    });
    return groups;
  }, [photos]);

  const photoTypes = React.useMemo(() => {
    const types = ["All"];
    TAB_ORDER.forEach((t) => groupedPhotos[t] && types.push(t));
    Object.keys(groupedPhotos).forEach((t) => !types.includes(t) && types.push(t));
    return types;
  }, [groupedPhotos]);

  const filteredPhotos =
    activeTab === "All" ? photos : groupedPhotos[activeTab] || [];

  const getTypeCount = (type) =>
    type === "All" ? photos.length : groupedPhotos[type]?.length || 0;

  // ── Effects ──────────────────────────────────────────────────────
  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Reset viewer state when the filter actually changes to a DIFFERENT
  // tab. We compare against the previous tab VALUE rather than using an
  // "is this the first run" boolean latch — a latch flips permanently
  // after one invocation, which breaks under React Strict Mode: Strict
  // Mode (dev only) deliberately invokes every effect twice to catch
  // missing-cleanup bugs, so a latch treats that harmless second
  // invocation as a real tab change and wipes out a correctly-resolved
  // `initialSelectedPhotoId` match back to index 0. Comparing values
  // instead means both Strict-Mode invocations (same activeTab, same
  // value) correctly no-op, while a genuine tab switch (different
  // value) still resets as intended.
  const prevActiveTabRef = useRef(activeTab);
  useEffect(() => {
    if (prevActiveTabRef.current === activeTab) {
      return; // not a real change — initial mount or a Strict Mode re-invoke
    }
    prevActiveTabRef.current = activeTab;
    setSelectedIndex(0);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
    if (gridScrollRef.current) gridScrollRef.current.scrollTop = 0;
  }, [activeTab]);

  // Keyboard: works in both modes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        if (viewMode === "lightbox") setViewMode("grid");
        else onClose();
        return;
      }
      if (viewMode !== "lightbox") return;
      if (e.key === "ArrowRight") handleNext();
      else if (e.key === "ArrowLeft") handlePrev();
      else if (e.key === "+" || e.key === "=") handleZoomIn();
      else if (e.key === "-") handleZoomOut();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, filteredPhotos.length, selectedIndex]);

  // Scroll active thumbnail into view in lightbox
  useEffect(() => {
    if (viewMode !== "lightbox") return;
    const el = thumbnailContainerRef.current?.querySelectorAll(".photo-thumb-item")[selectedIndex];
    el?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [selectedIndex, viewMode]);

  // ── Handlers ─────────────────────────────────────────────────────
  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const openLightbox = (index) => {
    setSelectedIndex(index);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
    setViewMode("lightbox");
  };

  const handlePrev = useCallback(() => {
    if (!filteredPhotos.length) return;
    setSelectedIndex((i) => (i - 1 + filteredPhotos.length) % filteredPhotos.length);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [filteredPhotos.length]);

  const handleNext = useCallback(() => {
    if (!filteredPhotos.length) return;
    setSelectedIndex((i) => (i + 1) % filteredPhotos.length);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [filteredPhotos.length]);

  const handleZoomIn = () => setZoomLevel((p) => Math.min(p + 0.25, 3));
  const handleZoomOut = () => {
    setZoomLevel((p) => {
      const next = Math.max(p - 0.25, 0.5);
      if (next <= 1) setPosition({ x: 0, y: 0 });
      return next;
    });
  };
  const handleZoomReset = () => {
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (zoomLevel <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  const handleMouseMove = (e) => {
    if (!isDragging || zoomLevel <= 1) return;
    setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e) => {
    if (zoomLevel <= 1) return;
    const t = e.touches[0];
    setIsDragging(true);
    setDragStart({ x: t.clientX - position.x, y: t.clientY - position.y });
  };
  const handleTouchMove = (e) => {
    if (!isDragging || zoomLevel <= 1) return;
    const t = e.touches[0];
    setPosition({ x: t.clientX - dragStart.x, y: t.clientY - dragStart.y });
  };
  const handleTouchEnd = () => setIsDragging(false);

  const currentPhoto = filteredPhotos[selectedIndex] || null;
  console.log("[PhotoGalleryModal] render →", { selectedIndex, currentPhotoId: currentPhoto?.PhotoID, currentPhotoUrl: currentPhoto?.PhotoURL });

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div
      className={`photo-gallery-overlay mode-${viewMode}`}
      ref={overlayRef}
      onClick={handleOverlayClick}
    >
      <div className="photo-gallery-modal">
        {/* Sticky header — filters + close */}
        <div className="photo-gallery-header">
          <div className="photo-gallery-tabs" role="tablist">
            {photoTypes.map((type) => (
              <button
                key={type}
                role="tab"
                aria-selected={activeTab === type}
                className={`photo-tab ${activeTab === type ? "active" : ""}`}
                onClick={() => setActiveTab(type)}
              >
                <span>{type === "All" ? "All Photos" : formatTypeName(type)}</span>
                <span className="photo-tab-count">{getTypeCount(type)}</span>
              </button>
            ))}
          </div>

          <div className="photo-gallery-header-actions">
            {viewMode === "lightbox" && (
              <button
                className="photo-gallery-icon-btn"
                onClick={() => setViewMode("grid")}
                aria-label="Back to grid"
                title="Back to grid"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                </svg>
              </button>
            )}
            <button className="photo-gallery-icon-btn photo-gallery-close" onClick={onClose} aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                <path d="M2 2L18 18M18 2L2 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        {viewMode === "grid" ? (
          <div className="photo-grid-scroll" ref={gridScrollRef}>
            {filteredPhotos.length === 0 ? (
              <div className="photo-grid-empty">
                <p>No photos in this category</p>
              </div>
            ) : (
              <div className="photo-masonry">
                {filteredPhotos.map((photo, index) => {
                  const badge = typeBadgeLetter(photo.PhotoType);
                  return (
                    <button
                      key={photo.PhotoID || index}
                      type="button"
                      className="masonry-item"
                      onClick={() => openLightbox(index)}
                      aria-label={photo.PhotoTitle || `Open photo ${index + 1}`}
                    >
                      {isVideoUrl(photo.PhotoURL) ? (
                        <VideoThumb src={photo.PhotoURL} className="masonry-item-video" />
                      ) : (
                        <img
                          src={photo.PhotoURL}
                          alt={photo.PhotoTitle || `Photo ${index + 1}`}
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <span className="masonry-item-overlay">
                        <span className="masonry-item-type">
                          {formatTypeName(photo.PhotoType || "PG")}
                        </span>
                        <span className="masonry-item-expand" aria-hidden>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 3 21 3 21 9" />
                            <polyline points="9 21 3 21 3 15" />
                            <line x1="21" y1="3" x2="14" y2="10" />
                            <line x1="3" y1="21" x2="10" y2="14" />
                          </svg>
                        </span>
                      </span>
                      {isVideoUrl(photo.PhotoURL) && (
                        <span className="masonry-item-play-badge" aria-hidden="true">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </span>
                      )}
                      {badge && <span className="masonry-item-badge">{badge}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="photo-lightbox">
            <div
              className="photo-main-area"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              ref={imageContainerRef}
            >
              {currentPhoto ? (
                <>
                  <div className="photo-image-info">
                    {currentPhoto.PhotoType && (
                      <span className="photo-image-type">
                        {formatTypeName(currentPhoto.PhotoType)}
                      </span>
                    )}
                  </div>

                  <button className="photo-nav-btn photo-nav-prev" onClick={handlePrev} aria-label="Previous">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  <div
                    className="photo-main-wrapper"
                    style={{
                      transform: isVideoUrl(currentPhoto.PhotoURL)
                        ? "none"
                        : `translate(${position.x}px, ${position.y}px) scale(${zoomLevel})`,
                      cursor: zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "default",
                      transition: isDragging ? "none" : "transform 0.2s ease",
                    }}
                  >
                    {isVideoUrl(currentPhoto.PhotoURL) ? (
                      <video
                        key={currentPhoto.PhotoURL}
                        src={currentPhoto.PhotoURL}
                        className="photo-main-video"
                        controls
                        autoPlay
                        playsInline
                      />
                    ) : (
                      <img
                        src={currentPhoto.PhotoURL}
                        alt={currentPhoto.PhotoTitle || "Photo"}
                        className="photo-main-image"
                        draggable={false}
                      />
                    )}
                  </div>

                  <button className="photo-nav-btn photo-nav-next" onClick={handleNext} aria-label="Next">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                      <path d="M9 18L15 12L9 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  <div className="photo-counter">
                    {selectedIndex + 1} / {filteredPhotos.length}
                  </div>

                  {!isVideoUrl(currentPhoto.PhotoURL) && (
                    <div className="photo-controls">
                      <button className="photo-control-btn" onClick={handleZoomOut} title="Zoom out" aria-label="Zoom out">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                          <line x1="8" y1="11" x2="14" y2="11" />
                        </svg>
                      </button>
                      <button className="photo-control-btn" onClick={handleZoomReset} title="Reset" aria-label="Reset zoom">
                        <span style={{ fontSize: 11, fontWeight: 600 }}>
                          {Math.round(zoomLevel * 100)}%
                        </span>
                      </button>
                      <button className="photo-control-btn" onClick={handleZoomIn} title="Zoom in" aria-label="Zoom in">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8" />
                          <line x1="21" y1="21" x2="16.65" y2="16.65" />
                          <line x1="11" y1="8" x2="11" y2="14" />
                          <line x1="8" y1="11" x2="14" y2="11" />
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="photo-main-empty">
                  <p>No photos available</p>
                </div>
              )}
            </div>

            <div className="photo-thumbnails-container" ref={thumbnailContainerRef}>
              <div className="photo-thumbnails">
                {filteredPhotos.map((photo, index) => (
                  <button
                    key={photo.PhotoID || index}
                    type="button"
                    className={`photo-thumb-item ${selectedIndex === index ? "active" : ""}`}
                    onClick={() => {
                      setSelectedIndex(index);
                      setZoomLevel(1);
                      setPosition({ x: 0, y: 0 });
                    }}
                    aria-label={`View photo ${index + 1}`}
                  >
                    {isVideoUrl(photo.PhotoURL) ? (
                      <VideoThumb src={photo.PhotoURL} />
                    ) : (
                      <img src={photo.PhotoURL} alt="" loading="lazy" />
                    )}
                    {isVideoUrl(photo.PhotoURL) && (
                      <span className="photo-thumb-play-badge" aria-hidden="true">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="#fff">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoGalleryModal;